import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
    catchError,
    concatMap,
    flatMap,
    map,
    switchMap,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import { ThirdPartyPayrollService } from '../third-party.service';
import * as fromShared from '../../../shared';
import * as ThirdPartyPayrollActions from './third-party-payroll.actions';
import { DisplayMessageMinimum, ResponseStatus } from '../../../shared';
import { UnsavedChangesService } from '../../../shared/modal-dialog';
import {
    EntityActionFactory,
    EntityCollectionService,
    EntityOp,
    EntityServices
} from '@ngrx/data';
import * as fromDataServices from '../../../data-services';
import { Store } from '@ngrx/store';
import { LoggingService } from '../../../shared/services/logging';
import * as fromSettings from '../../../company-setting';
import { of } from 'rxjs';
import moment from 'moment';

const log = new LoggingService();
@Injectable()
export class ThirdPartyPayrollEffects {
    PayrollExportModelDS: EntityCollectionService<fromDataServices.PayrollExport.PayrollExportModel>;
    constructor(
        private actions$: Actions,
        private service: ThirdPartyPayrollService,
        private confirmservice: UnsavedChangesService,
        private toaster: fromShared.MessageService,
        public entityServices: EntityServices
    ) {
        this.PayrollExportModelDS =
            entityServices.getEntityCollectionService<fromDataServices.PayrollExport.PayrollExportModel>(
                fromDataServices.PayrollExport.entityName
            );
        log.verbose({
            type: 'ThirdPartyPayrollEffects::',
            entityServices,
            PayrollExportModelDS: this.PayrollExportModelDS
        });
        this.syncGLFailure$ = createEffect(
            () => {
                log.verbose({
                    type: 'ThirdPartyPayrollEffects:syncGLFailure',
                    message: 'create'
                });
                return this.actions$.pipe(
                    ofType(ThirdPartyPayrollActions.syncGLFailure),
                    withLatestFrom(this.PayrollExportModelDS.entities$),
                    withLatestFrom(
                        this.actions$.pipe(
                            ofType(ThirdPartyPayrollActions.syncGL)
                        )
                    ),
                    map(x => {
                        log.error({
                            type: 'ThirdPartyPayrollEffects:syncGLFailure:map',
                            x
                        });
                        x[0][1] = x[0][1]
                            .filter(x1 => x1.id === x[0][0].id)
                            .map(x1 => ({
                                ...x1,
                                Status: {
                                    StatusCode: ResponseStatus.Failed,
                                    Message:
                                        x[0][0]?.e?.status === 404
                                            ? 'No GL entries found'
                                            : x[0][0]?.e?.error
                                                  ?.ExceptionMessage ||
                                              x[0][0]?.e?.error?.Message ||
                                              x[0][0]?.e?.statusText
                                }
                            }));
                        return x;
                    }),
                    /** An EMPTY observable only emits completion. Replace with your own observable stream */
                    switchMap(([[action, exp], req]) => {
                        // switchMap(action => {
                        log.error({
                            type: 'ThirdPartyPayrollEffects:syncGLFailure',
                            action,
                            e: action.e,
                            c: action.c,
                            exp
                        });
                        this.PayrollExportModelDS.updateManyInCache(exp);
                        this.toaster.add({
                            severity: 'error',
                            summary: 'Error',
                            life: 0,
                            // detail: 'GL Sync Complete'
                            detail: `GL Sync for ${moment(req.endDate).format(
                                'MM/DD/YYYY'
                            )} - ${
                                exp[0]?.Status?.Message ||
                                action?.e?.error?.ExceptionMessage ||
                                action?.e?.error?.Message
                            }`
                        });
                        return action.c;
                        // return fromShared.Errors.handleServiceError(
                        //     action.e,
                        //     action.c
                        // );
                    })
                );
            },
            { dispatch: false }
        );
    }
    getThirdPartyPayrolls$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ThirdPartyPayrollActions.getThirdPartyPayrolls),
            switchMap(() =>
                this.service.get().pipe(
                    map(data =>
                        ThirdPartyPayrollActions.loadThirdPartyPayrolls({
                            thirdPartyPayrolls: data
                        })
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    getPayrollCodes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ThirdPartyPayrollActions.getPayrollCodes),
            switchMap(() =>
                this.service.getPayrollCodes().pipe(
                    flatMap(data => [
                        new fromShared.Errors.SetErrors(
                            [
                                ...data
                                    .filter(
                                        r =>
                                            r.Status.StatusCode >=
                                            DisplayMessageMinimum
                                    )
                                    .map(a => a.Status)
                            ].filter(fromShared.Errors.filterErrors)
                        ),
                        ThirdPartyPayrollActions.loadPayrollCodes({
                            payrollCodes: data
                        })
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    getReimbursementPaycodes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ThirdPartyPayrollActions.getReimbursementPaycodes),
            switchMap(() =>
                this.service.getReimbursementPaycodes().pipe(
                    flatMap(data => [
                        ThirdPartyPayrollActions.loadReimbursementPaycodes({
                            reimbursementPaycodes: data
                        }),
                        fromSettings.getCompanySetting({
                            SettingType:
                                fromSettings.CompanySettingEnum
                                    .ExpenseReimbursementPaycode
                        })
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    putPayrollCodes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ThirdPartyPayrollActions.putPayrollCodes),
            switchMap(action =>
                this.service.putPayrollCodes(action.payrollCodes).pipe(
                    concatMap(resp =>
                        fromShared.Errors.IsSuccessStatus(resp.StatusCode)
                            ? [
                                  ThirdPartyPayrollActions.loadPayrollCodes({
                                      payrollCodes: resp.Items
                                  }),
                                  new fromShared.Errors.RemoveErrors(
                                      resp.Items.filter(
                                          x =>
                                              x.Status.StatusCode ===
                                              ResponseStatus.Success
                                      ).map(x => x.Paycodeid.toString())
                                  )
                              ]
                            : [
                                  new fromShared.Errors.SetErrors(
                                      fromShared.Errors.MapErrorsToStatus(resp)
                                  ),
                                  ThirdPartyPayrollActions.loadPayrollCodes({
                                      payrollCodes: resp.Items
                                  })
                              ]
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    confirmRemovePayrollCode$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ThirdPartyPayrollActions.confirmRemovePayrollCode),
            switchMap(action =>
                this.confirmservice
                    .confirm({
                        dialogText:
                            'Are you sure you want to delete this Payroll Code?'
                    })
                    .pipe(
                        map(x =>
                            x
                                ? ThirdPartyPayrollActions.removePayrollCode({
                                      payrollId: action.payrollCode.Paycodeid
                                  })
                                : ThirdPartyPayrollActions.loadPayrollCodes({
                                      payrollCodes: [
                                          {
                                              ...action.payrollCode,
                                              Status: {
                                                  StatusCode:
                                                      ResponseStatus.None,
                                                  Message: ''
                                              }
                                          }
                                      ]
                                  })
                        )
                    )
            )
        )
    );
    removePayrollCode$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ThirdPartyPayrollActions.removePayrollCode),
            switchMap(action =>
                this.service.deletePayrollCode(action.payrollId).pipe(
                    concatMap(resp =>
                        fromShared.Errors.IsSuccessStatus(resp.StatusCode)
                            ? [
                                  ThirdPartyPayrollActions.deletePayrollCodes({
                                      payrollIds: resp.Items.map(
                                          x => x.EntityID as number
                                      )
                                  }),
                                  new fromShared.Errors.RemoveErrors(
                                      resp.Items.filter(
                                          x =>
                                              x.Status.StatusCode ===
                                              ResponseStatus.Success
                                      ).map(x => x.EntityID.toString())
                                  )
                              ]
                            : [
                                  new fromShared.Errors.SetErrors(
                                      fromShared.Errors.MapErrorsToStatus(resp)
                                  )
                              ]
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    putFileFormat$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ThirdPartyPayrollActions.putFileFormat),
            switchMap(action =>
                this.service.putFileFormats(action.fileFormats).pipe(
                    concatMap(resp =>
                        fromShared.Errors.IsSuccessStatus(resp.StatusCode)
                            ? [
                                  ThirdPartyPayrollActions.loadFileFormat({
                                      fileFormats: resp.Items
                                  }),
                                  new fromShared.Errors.RemoveErrors(
                                      resp.Items.filter(
                                          x =>
                                              x.Status.StatusCode ===
                                              ResponseStatus.Success
                                      ).map(x => x.Fieldid.toString())
                                  )
                              ]
                            : [
                                  new fromShared.Errors.SetErrors(
                                      fromShared.Errors.MapErrorsToStatus(resp)
                                  ),
                                  ThirdPartyPayrollActions.loadFileFormat({
                                      fileFormats: resp.Items
                                  })
                              ]
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    getFileFormat$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ThirdPartyPayrollActions.getFileFormat),
            switchMap(() =>
                this.service.getFileFormat().pipe(
                    map(data =>
                        ThirdPartyPayrollActions.loadFileFormat({
                            fileFormats: data
                        })
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    getPayrollExport$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ThirdPartyPayrollActions.getPayrollExport),
            switchMap(action =>
                this.service
                    .getPayrollExport(
                        action.startDate,
                        action.endDate,
                        action.codeAsRow
                    )
                    .pipe(
                        map(data =>
                            ThirdPartyPayrollActions.loadThirdPartyPayrolls({
                                thirdPartyPayrolls: data
                            })
                        ),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        );
    });
    syncGL$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ThirdPartyPayrollActions.syncGL),
            switchMap(action =>
                this.service.syncGL(action.endDate).pipe(
                    tap(result =>
                        log.verbose({
                            type: 'ThirdPartyPayrollEffects:syncGL',
                            result
                        })
                    ),
                    map(result => {
                        log.verbose({
                            type: 'ThirdPartyPayrollEffects:syncGL:success',
                            result
                        });
                        return ThirdPartyPayrollActions.syncGLSuccess({
                            result
                        });
                    }),
                    catchError((e, c) => {
                        log.verbose({
                            type: 'ThirdPartyPayrollEffects:syncGL:failure',
                            e,
                            c
                        });
                        return of(
                            ThirdPartyPayrollActions.syncGLFailure({
                                e,
                                c,
                                id: action.id
                            })
                        );
                    })
                )
            )
        );
    });
    syncGLFailure$: any;
    syncGLSuccess$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(ThirdPartyPayrollActions.syncGLSuccess),
                withLatestFrom(
                    this.actions$.pipe(ofType(ThirdPartyPayrollActions.syncGL))
                ),
                map(([successAction, syncAction]) => {
                    this.toaster.add({
                        severity: 'success',
                        summary: 'Success',
                        life: 0,
                        detail: `GL Sync for ${moment(syncAction.endDate).format(
                            'MM/DD/YYYY'
                        )} complete`
                    });
                    const r = successAction.result;
                    if (r && r.unmatchedAccountLineCount > 0) {
                        this.toaster.add({
                            severity: 'warn',
                            summary: 'QuickBooks account check',
                            life: 0,
                            detail:
                                r.qbAccountMismatchWarning ||
                                'One or more GL account numbers from GovConPay do not match QuickBooks Online. Run the Journal Entry Payroll Processed report and fix account numbers before sending the journal entry.'
                        });
                    }
                })
            );
        },
        { dispatch: false }
    );
    sendPayrollExport$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ThirdPartyPayrollActions.sendPayrollExport),
            switchMap(action =>
                this.service.sendPayroll(action.endDate).pipe(
                    map(result => {
                        const af = new EntityActionFactory();
                        const action = af.create(
                            fromDataServices.PayrollExport.entityName,
                            EntityOp.SAVE_UPSERT_ONE_SUCCESS,
                            {
                                ...result
                            }
                        );
                        return action;
                    }),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    fetchPayrollExportStatus$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ThirdPartyPayrollActions.fetchPayrollExportStatus),
            switchMap(action =>
                this.service.fetchPayrollExportStatus(action.id).pipe(
                    map(result => {
                        const af = new EntityActionFactory();
                        // if (update.changes.SyncPayrollItems === true) {
                        const action = af.create(
                            fromDataServices.PayrollExport.entityName,
                            EntityOp.SAVE_UPSERT_ONE_SUCCESS,
                            {
                                ...result,
                                Status: {
                                    StatusCode: (Object as any)
                                        .values(
                                            fromDataServices.PayrollExport
                                                .PayrollExportCompletedSuccessStatuses
                                        )
                                        .includes(result.ExportStatus)
                                        ? ResponseStatus.Success
                                        : ResponseStatus.Failed,
                                    Message: result.ExportStatus
                                }
                                // id: result.id,
                                // changed: true,
                                // changes: {
                                //     ...result,
                                //     id: result.id
                                // }
                            }
                        );
                        return action;
                    }),
                    catchError((e, c) => {
                        let error;
                        if (e instanceof Function) {
                            error = e();
                        } else {
                            error = e;
                        }
                        this.confirmservice.confirm({
                            showCancel: false,
                            headerText: 'Error',
                            dialogText:
                                error &&
                                (error.message ||
                                    error.statusText ||
                                    (error.Error && error.Error.Message) ||
                                    (error.error && error.error.message) ||
                                    (error.error && error.error.statusText) ||
                                    error)
                        });

                        return fromShared.Errors.handleServiceError(
                            {
                                ...e,
                                MsgProcessID:
                                    fromShared.ProcessEnum.PayrollExport,
                                MsgEntityID: action.id
                            },
                            c
                        );
                    })
                )
            )
        );
    });
}
